import React, { ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { DeleteForever, Save } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'

import { useGetUserSettingQuery, useUpdateUserSettingMutation } from '../../../../api/combined'
import { useDeleteGameMutation, useUpdateGameMutation } from '../../../../api/core'
import GRCircularProgress from '../../../../components/GRCircularProgress/GRCircularProgress'
import { useAppDispatch } from '../../../../hooks/storeHooks'
import { UserSettingKeys } from '../../../account/types/UserSettings'
import { displaySnackBar } from '../../../snackbar'
import { SnackBarMessage } from '../../../snackbar/types/SnackBarMessage'
import { Game, GameVisibility } from '../../types/Game'
import { GameStageId } from '../../types/GameStageId'
import { GameAddNewModalStage } from '../GameAddNewModal/GameAddNewModal'
import GameGenreSelect from '../GameGenreSelect/GameGenreSelect'
import GameIconSelect from '../GameIconSelect/GameIconSelect'

/**
 * DeleteGameConfirmDialog
 */
interface DeleteGameConfirmDialogProps {
  loading?: boolean
  open: boolean
  title: string
  body: string | ReactNode
  game: Game
  onDeleteClick: (game: Game) => void
  onClose: () => void
}
const DeleteGameConfirmDialog: React.FC<DeleteGameConfirmDialogProps> = ({ game, onDeleteClick, onClose, open, title, body, loading }) => {
  const { t } = useTranslation()

  return (
    <Dialog open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>{loading ? <GRCircularProgress /> : body}</Box>
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Button startIcon={<DeleteForever />} color="warning" variant="contained" onClick={() => onDeleteClick(game)}>
                {t('common:yes')}
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={() => onClose()}>{t('common:cancel')}</Button>
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </Dialog>
  )
}

interface CompareGamesList {
  [id: string]: string[]
}

/**
 * GameEditModal Edit game based on input game info
 */
interface GameEditModalProps {
  open: boolean
  game: Game
  stages?: GameAddNewModalStage[]
  onClose: (game?: Game) => void
  onGameEditDone: (game: Game, isGameDeleted?: boolean) => void
}
const GameEditModal: React.FC<GameEditModalProps> = ({ open = false, game, stages, onClose, onGameEditDone }) => {
  const { t } = useTranslation()

  const { data: compareGamesList, isLoading: isCompareGamesListLoading } = useGetUserSettingQuery<{ data: CompareGamesList; isLoading: boolean }>(
    UserSettingKeys.compareGamesList
  )
  const [saveUserSetting] = useUpdateUserSettingMutation()

  const dispatch = useAppDispatch()
  const showError = (errorPayload: SnackBarMessage) => dispatch(displaySnackBar(errorPayload))

  const gameStages = !!stages
    ? stages
    : [
        {
          label: t('game-analyzer:my_portfolio_concepts'),
          id: GameStageId.concept,
        },
        {
          label: t('game-analyzer:my_portfolio_productions'),
          id: GameStageId.production,
        },
        {
          label: t('game-analyzer:my_portfolio_lives'),
          id: GameStageId.live,
        },
      ]

  const [updateGame, { isLoading: isGameEditing }] = useUpdateGameMutation()
  const [deleteGame] = useDeleteGameMutation()

  const [metaData, setMetaData] = useState<Game>(game)
  const [disabled, setDisabled] = useState(false)

  const [openDeleteGameConfirmDialog, setOpenDeleteGameConfirmDialog] = useState(false)
  // Track the entire delete flow (mutation + downstream user-setting cleanup)
  // so the confirmation dialog's loading spinner stays visible until the
  // parent modal fully unmounts. Without this, the buttons re-appear in the
  // gap between `deleteGame` resolving and `saveUserSetting` resolving when
  // the deleted game is in Compare Games (GR-2370).
  const [isDeleting, setIsDeleting] = useState(false)

  const resetMetaData = () => {
    setMetaData(game)
  }

  const onSave = async () => {
    try {
      await updateGame({ game: metaData, gameId: game.id }).unwrap()
      onGameEditDone(game)
    } catch (err) {
      showError({ message: t('game-edit:error_edit_game_body'), severity: 'error', open: true })
    }
  }

  const onCloseClick = () => {
    onClose(game)
    resetMetaData()
  }

  const onGameDeleteClick = async () => {
    setIsDeleting(true)
    try {
      await deleteGame(game.id).unwrap()

      // Notify the parent immediately after the delete succeeds so it can suppress
      // any "Analysis not found!" notification triggered by the subsequent query
      // refetch on the now-deleted game.
      onGameEditDone(game, true)

      if (compareGamesList && compareGamesList[game.id]) {
        const updatedCompareGamesList = { ...compareGamesList }
        delete updatedCompareGamesList[game.id]

        await saveUserSetting({
          settingKey: UserSettingKeys.compareGamesList,
          value: updatedCompareGamesList,
        }).unwrap()
      }

      dispatch(displaySnackBar({ message: t('game-edit:game_deleted'), severity: 'success', open: true }))
    } catch (err) {
      setOpenDeleteGameConfirmDialog(false)
      setIsDeleting(false)
      showError({ message: t('game-edit:error_edit_game_body'), severity: 'error', open: true })
    }
  }

  const validateMetaData = (): boolean => {
    return JSON.stringify(game) !== JSON.stringify(metaData)
  }

  const onDataChange = (e: React.FormEvent<HTMLFormElement>) => {
    const target = e.target as HTMLTextAreaElement

    if (target.name === 'name') {
      target.value.length < 1 ? setDisabled(true) : setDisabled(false)
    }

    switch (target.name) {
      case 'stageId':
        setMetaData({ ...metaData, [target.name]: Number(target.value) } as Game)
        break
      default:
        setMetaData({ ...metaData, [target.name]: target.value } as Game)
    }
  }

  return (
    <>
      <DeleteGameConfirmDialog
        open={openDeleteGameConfirmDialog}
        title={t('game-edit:delete_game')}
        body={<Trans i18nKey="game-edit:confirm_delete_game" values={{ gameName: game.resolvedName }} />}
        game={game}
        onDeleteClick={onGameDeleteClick}
        onClose={() => setOpenDeleteGameConfirmDialog(false)}
        loading={isDeleting}
      />
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>{t('game-edit:game_settings')}</DialogTitle>
        <DialogContent aria-disabled dividers sx={{ position: 'relative' }}>
          {isGameEditing && (
            <div className="GameAddNewModal__loadingCover">
              <GRCircularProgress fontSize={1} />
            </div>
          )}
          <Grid container columnSpacing={2} mt={2}>
            <Grid item xs={10}>
              <Box component="form" flexDirection="column" display="flex" noValidate autoComplete="off" onChange={onDataChange}>
                <TextField
                  required
                  autoFocus
                  name="name"
                  size="small"
                  label={t('common:name')}
                  value={metaData.name}
                  variant="outlined"
                  color="primary"
                  sx={{ mb: 2 }}
                  error={disabled}
                />

                <TextField
                  name="desc"
                  label={t('common:description')}
                  value={metaData.desc ? metaData.desc : ''}
                  variant="outlined"
                  color="primary"
                  sx={{ mb: 2 }}
                  multiline
                  rows={4}
                  size="small"
                />

                <Box sx={{ mb: 2 }}>
                  <GameGenreSelect
                    value={metaData.conventionalSubgenreId || ''}
                    onChange={(e) => {
                      setMetaData({ ...metaData, conventionalSubgenreId: e.target.value } as Game)
                    }}
                    showSubgenreDefinitionBtn
                  />
                </Box>

                <Box sx={{ mb: 1 }}>
                  <FormLabel>{t('game-edit:development_stage')}</FormLabel>
                  <RadioGroup aria-labelledby="development-stage" row value={metaData.stageId} name="stageId">
                    {gameStages.map((stage) => (
                      <FormControlLabel
                        sx={{ textTransform: 'capitalize' }}
                        key={stage.id}
                        itemType="number"
                        value={stage.id}
                        control={<Radio />}
                        label={stage.label}
                      />
                    ))}
                  </RadioGroup>
                </Box>

                <FormLabel>{t('game-edit:visibility')}</FormLabel>
                <RadioGroup row value={metaData.visibility} name="visibility">
                  <FormControlLabel value={GameVisibility.organization} control={<Radio />} label={t('game-edit:share_with_team') as string} />
                  <FormControlLabel value={GameVisibility.private} control={<Radio />} label={t('game-edit:private') as string} />
                </RadioGroup>
              </Box>
            </Grid>

            <Grid item xs={2}>
              <Box sx={{ mb: 1 }}>
                <FormLabel>{t('common:icon')}</FormLabel>
              </Box>
              <GameIconSelect
                selectedIcon={game.icon}
                onFileSelected={(icon) => {
                  setMetaData({ ...metaData, icon } as Game)
                }}
                onFileRemoved={() => {
                  setMetaData({ ...metaData, icon: null } as Game)
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="space-between">
            <Grid item xs={3}>
              <Button
                disabled={isGameEditing || isCompareGamesListLoading}
                startIcon={<DeleteForever />}
                color="warning"
                variant="contained"
                onClick={() => setOpenDeleteGameConfirmDialog(true)}
              >
                {t('game-edit:delete_game')}
              </Button>
            </Grid>

            <Grid item xs={6} display="flex" justifyContent="center" alignContent="center">
              <Typography color="GrayText" alignContent="center">{`${t('game-edit:created_by')} ${game.createdBy}`}</Typography>
            </Grid>

            <Grid item xs={3} display="flex" justifyContent="flex-end" alignContent="center">
              <Button sx={{ marginRight: 1 }} onClick={onCloseClick}>
                {t('common:cancel')}
              </Button>

              <Button endIcon={<Save />} disabled={!validateMetaData() || isGameEditing || disabled} variant="contained" onClick={onSave}>
                {t('common:save')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default GameEditModal
